import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Page from '../../components/page/page'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import { DateFilters, DatePeriod, GlobalNotification } from '../../model'
import {
  ComunicationExternalsPage,
  ComunicationFilesPage,
  ComunicationProceduresPage,
  ComunicationSendsPage
} from './tabs'

import {
  storeActiveTabForCurrentPage,
  storeCustomFilterForCurrentPage,
  storeDateFilterForCurrentPage,
  getActiveCompany
} from '../../redux'

import { hasPermission } from '../../helpers/permissionsHelper'
import { Resources } from '../../model/RBAC/constants'

import { DataType, Input, SelectOption, UserPermission } from '@globaleris/ui.library'
import { EnumGrupoDocumento } from '../../api'
import PageHeader from '../../components/page/pageHeader'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { usePageState } from '../../hooks'
import { fetchDocumentsTypesByDocumentGroupList } from '../documents/documents.service'
import { Comunications } from './comunicationCenter.model'
import { fetchExternalsListItems } from './tabs/externals/comunicationExternals.service'
import { fetchFilesListItems } from './tabs/files/comunicationFiles.service'
import { fetchProceduresListItemsByDates } from './tabs/procedures/comunicationProdecures.service'
import { fetchSendsListItems } from './tabs/sends/comunicationSends.service'

export interface ComunicationCenterPageProps {}

export const ComunicationCenterPage: React.FC<ComunicationCenterPageProps> = () => {
  const [t] = useTranslation(['globalerisLibrary', 'comunicationCenter', 'enums'])

  const { dateFilter, customFilter, activeTab } = usePageState()

  const [currentTab, setCurrentTab] = React.useState<number>(activeTab ?? 0)

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [datePeriod, setDatePeriod] = useState<DatePeriod>({
    start: customFilter?.startDate
      ? customFilter?.startDate!
      : dateFilter
        ? moment(dateFilter.initDate).toDate()
        : moment(new Date()).subtract(7, 'days').toDate(),
    end: customFilter?.endDate
      ? customFilter?.endDate!
      : dateFilter
        ? moment(dateFilter.endDate).toDate()
        : moment().toDate(),

    dateFilter: dateFilter?.activeDateFilter ?? undefined,
    customFilter: customFilter ?? undefined
  })

  const [controlledActiveFilter, setControlledActiveFilter] = useState<DateFilters | undefined>()
  const [documentTypeFilterOptions, setDocumentTypeFilterOptions] = useState<
    SelectOption[] | undefined
  >()

  const tabsDefinition: Map<number, Tab> = new Map([
    [
      Comunications.FILES,
      {
        label: t('comunicationCenter:tabs.files'),
        hasPermission: hasPermission(Resources.ComunicationsCenterFiles, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ]),
        id: Comunications.FILES
      }
    ],
    [
      Comunications.SENDS,
      {
        label: t('comunicationCenter:tabs.sends'),
        hasPermission: hasPermission(Resources.ComunicationsCenterSends, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ]),
        id: Comunications.SENDS
      }
    ],
    [
      Comunications.EXTERNALS,
      {
        label: t('comunicationCenter:tabs.externals'),
        hasPermission: hasPermission(Resources.ComunicationsCenterExternals, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ]),
        id: Comunications.EXTERNALS
      }
    ],
    [
      Comunications.PROCEDURES,
      {
        label: t('comunicationCenter:tabs.procedures'),
        hasPermission: hasPermission(Resources.ComunicationsCenterProcedures, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ]),
        id: Comunications.PROCEDURES
      }
    ]
  ])

  // filter tabs with permissions to avoid getting hidden tabs values onTabChange (BUG #61469)
  const filteredTabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  const company = getActiveCompany()?.description

  React.useEffect(() => {
    activeTab && setCurrentTab(activeTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNotifications = notification => {
    setGlobalNotification(notification)
  }

  const renderComunication = (tabIndex: number | undefined) => {
    const comunicationType = filteredTabsArray[tabIndex ?? currentTab]?.id
    switch (comunicationType) {
      case Comunications.FILES:
        return (
          <ComunicationFilesPage
            onNotificationChange={p => {
              handleNotifications(p)
            }}
            onFetchingChange={f => {
              setIsFetching(f)
            }}
            dateRange={datePeriod}
            customFilter={customFilter}
          ></ComunicationFilesPage>
        )
      case Comunications.SENDS:
        return (
          <ComunicationSendsPage
            onNotificationChange={p => {
              handleNotifications(p)
            }}
            onFetchingChange={f => {
              setIsFetching(f)
            }}
            dateRange={datePeriod}
          />
        )
      case Comunications.EXTERNALS:
        return (
          <ComunicationExternalsPage
            onNotificationChange={p => {
              handleNotifications(p)
            }}
            dateRange={datePeriod}
            onFetchingChange={f => {
              setIsFetching(f)
            }}
          />
        )
      //case EnumComunications.Alerts:
      //  return <ComunicationAlertsPage></ComunicationAlertsPage>;
      case Comunications.PROCEDURES:
        return (
          <ComunicationProceduresPage
            onNotificationChange={p => {
              handleNotifications(p)
            }}
            dateRange={datePeriod}
            onFetchingChange={f => {
              setIsFetching(f)
            }}
          />
        )
      default:
        return null
    }
  }

  const handleDateFilterChange = async (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): Promise<void> => {
    setDatePeriod({ start: startDate, end: endDate })
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  const handleActiveFilters = async (index: number) => {
    if (
      index !== Comunications.FILES &&
      dateFilter?.activeDateFilter === DateFilters.RECORDS_LIM_500
    ) {
      setControlledActiveFilter(DateFilters.LASTWEEK)
    } else {
      setControlledActiveFilter(undefined)
    }
  }

  const countItems = async (
    startDate: Date,
    endDate: Date,
    customFilter?: any
  ): Promise<number | undefined> => {
    const countHelpers = {
      [Comunications.FILES]: fetchFilesListItems,
      [Comunications.SENDS]: fetchSendsListItems,
      [Comunications.EXTERNALS]: fetchExternalsListItems,
      [Comunications.PROCEDURES]: fetchProceduresListItemsByDates
    }
    const dateRange = {
      start: startDate,
      end: endDate,
      customFilter: customFilter
    }
    return await countHelpers[currentTab || Comunications.FILES](dateRange).then(response => {
      if (response.success) {
        if (response.data !== undefined) {
          return response.data.length
        } else {
          return 0
        }
      } else {
        return undefined
      }
    })
  }

  const fileAditionalFilters: Input[] = [
    {
      name: 'documentType',
      label: t('translation:components.searchFilter.inputs.documentType'),
      type: DataType.SELECT,
      selectOptions: documentTypeFilterOptions ?? []
    }
  ]

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    const totalCount = await countItems(
      filterToApply.startDate,
      filterToApply.endDate,
      filterToApply
    )
    if (totalCount !== undefined && totalCount < 1000) {
      return true
    }
    return false
  }

  const onApplyCustomFilter = async (filterToApply: any) => {
    setDatePeriod({
      start: filterToApply.startDate,
      end: filterToApply.endDate,
      dateFilter: filterToApply.activeDateFilter
    })
    storeCustomFilterForCurrentPage(filterToApply)
  }

  let filters = [
    {
      filter: DateFilters.TODAY,
      label: t('translation:components.dateFilter.todayNoGas')
    },
    {
      filter: DateFilters.YESTERDAY,
      label: t('translation:components.dateFilter.yesterday')
    },
    {
      filter: DateFilters.LASTWEEK,
      label: t('translation:components.dateFilter.lastWeek')
    }
  ]

  if (currentTab === Comunications.FILES) {
    filters.push({
      filter: DateFilters.RECORDS_LIM_500,
      label: t('translation:components.dateFilter.documentsLim500')
    })
  }

  useEffect(() => {
    const fetchDocumentTypeListOptions = async () => {
      if (currentTab === Comunications.FILES && documentTypeFilterOptions === undefined) {
        const documentGroupList = [
          EnumGrupoDocumento.SPLG,
          EnumGrupoDocumento.ProvisionInformacion,
          EnumGrupoDocumento.DescargaPlantillas
        ]
        fetchDocumentsTypesByDocumentGroupList(documentGroupList).then(response => {
          if (response.success) {
            if (company != 'ENERGYA VM') {
              //Si la compañía no es 'ENERGYA VM', se excluyen los tipos de documentos especificos para esta.
              const excludedIdsSet = new Set<string | number>([186, 197])

              response.data = (response.data || []).filter(item => !excludedIdsSet.has(item.value))
            }
            setDocumentTypeFilterOptions(response.data)
          }
        })
      }
    }
    fetchDocumentTypeListOptions()
  }, [currentTab, documentTypeFilterOptions])

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('comunicationCenter:title')}>
        <DateFilter
          inactive={customFilter}
          enabled={!isFetching}
          initialFilter={
            dateFilter === undefined ||
            dateFilter?.activeDateFilter === null ||
            dateFilter?.activeDateFilter === undefined
              ? DateFilters.LASTWEEK
              : dateFilter.activeDateFilter
          }
          controlledActiveFilter={controlledActiveFilter}
          onSelectDate={handleDateFilterChange}
          filters={filters}
        ></DateFilter>
        <CustomFilter
          onApplyFilter={onApplyCustomFilter}
          onPrevalidateFilter={onPrevalidateCustomFilter}
          appliedFilter={customFilter}
          enabled={!isFetching}
          additionalFilters={currentTab === Comunications.FILES ? fileAditionalFilters : undefined}
        ></CustomFilter>
      </PageHeader>
      <TabsWithPermissions
        tabs={tabsDefinition}
        tabDefault={currentTab}
        onTabChange={index => {
          if (currentTab !== index) {
            setCurrentTab(index)
            storeActiveTabForCurrentPage(index)
            handleActiveFilters(index)
          }
        }}
      ></TabsWithPermissions>
      {renderComunication(currentTab)}
    </Page>
  )
}
